import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { resolveMedia } from '../../context/SettingsContext';

export default function Cart() {
  const { items, updateQty, removeItem, itemsTotal } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="bg-[#FAF8F5] min-h-screen py-24">
        <div className="max-w-md mx-auto px-4 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-[#F5EFEB] flex items-center justify-center text-[#C49A45] mx-auto border border-[#E8DFD8]">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#2B1810]">Your Shopping Cart is Empty</h2>
          <p className="text-xs text-[#8A796C]">Explore our catalog of 100% authentic sandalwood idols, bracelets, malas & agarbatti.</p>
          <div>
            <Link to="/shop" className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-widest px-8 py-3 rounded shadow transition-colors inline-block">
              CONTINUE SHOPPING
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF8F5] min-h-screen text-[#2B1810] py-10">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="font-heading text-3xl font-bold text-[#2B1810] mb-8">
          Shopping Cart ({items.length})
        </h1>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-4">
            {items.map((item) => (
              <div 
                key={item.productId + (item.variantId || '')} 
                className="bg-white border border-[#E8DFD8] rounded-lg p-4 flex gap-4 shadow-sm items-center"
              >
                <div className="w-20 h-20 bg-[#FAF8F5] rounded border border-[#E8DFD8] overflow-hidden shrink-0">
                  <img 
                    src={resolveMedia(item.image)} 
                    alt={item.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = '/assets/ganesha_idol.jpg'; }}
                  />
                </div>

                <div className="flex-1">
                  <h3 className="font-heading text-sm font-bold text-[#2B1810]">{item.name}</h3>
                  {item.variantLabel && <p className="text-xs text-[#8A796C] mt-0.5">{item.variantLabel}</p>}
                  <p className="font-bold text-sm text-[#2B1810] mt-1">₹{item.price.toFixed(2)}</p>

                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center border border-[#E0D6CC] rounded bg-white">
                      <button
                        className="px-2.5 py-1 text-xs font-bold text-[#2B1810] hover:bg-[#FAF8F5]"
                        onClick={() => updateQty(item.productId, item.variantId, Math.max(1, item.qty - 1))}
                      >
                        −
                      </button>
                      <span className="px-3 text-xs font-bold text-[#2B1810]">{item.qty}</span>
                      <button
                        className="px-2.5 py-1 text-xs font-bold text-[#2B1810] hover:bg-[#FAF8F5]"
                        onClick={() => updateQty(item.productId, item.variantId, item.qty + 1)}
                      >
                        +
                      </button>
                    </div>

                    <button
                      onClick={() => removeItem(item.productId, item.variantId)}
                      className="text-xs text-red-700 hover:underline font-semibold"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <span className="font-bold text-base text-[#2B1810]">
                    ₹{(item.price * item.qty).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#E8DFD8] rounded-lg p-6 h-fit shadow-sm space-y-4">
            <h3 className="font-heading text-base font-bold text-[#2B1810] border-b border-[#E8DFD8] pb-3">
              Order Summary
            </h3>

            <div className="flex justify-between text-sm font-semibold text-[#2B1810]">
              <span>Subtotal</span>
              <span>₹{itemsTotal.toFixed(2)}</span>
            </div>

            <p className="text-xs text-[#8A796C] leading-relaxed">
              Shipping distance and fees calculated at checkout based on live warehouse origin.
            </p>

            <button 
              onClick={() => navigate('/checkout')} 
              className="w-full bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-widest py-3.5 rounded shadow transition-colors"
            >
              PROCEED TO CHECKOUT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
